import moment from 'moment';

// How many rows a section shows at a time. Scrolling to the bottom of a section
// reveals the next batch; Compared and Not compared page independently.
export const TREND_PAGE_SIZE = 10;

// Round labels are month-level ("Apr 2025") — a 360 is identified by the month
// it closed in, never by an exact day.
export const ROUND_LABEL_FORMAT = 'MMM YYYY';

export const SECTION_TEXT = {
    compared: {
        title: 'Compared',
        empty: 'No compared participants match this search.',
        loading: 'Loading participants…',
        error: 'Couldn’t load compared participants. Try again.',
    },
    notCompared: {
        title: 'Not compared',
        empty: 'No not-compared participants match this search.',
        loading: 'Loading participants…',
        error: 'Couldn’t load not-compared participants. Try again.',
    },
};

// Which of a participant's earlier 360s becomes their baseline. A per-row
// dropdown pick overrides this for that one participant.
export const MATCH_MODES = {
    PREVIOUS: 'previous',
    FIRST: 'first',
};

// How a pairing starts out, matching trendPairing in the reducer's initial
// state: everyone on their previous 360, nobody re-pointed by hand. A fetch that
// isn't given a pairing is one made right after a reset, so it uses this.
export const INITIAL_TREND_PAIRING = {
    matchMode: MATCH_MODES.PREVIOUS,
    baselineOverrides: {},
};

export const MATCH_MODE_OPTIONS = [
    { value: MATCH_MODES.PREVIOUS, label: 'their previous 360' },
    { value: MATCH_MODES.FIRST, label: 'their very first 360' },
];

export const CONTROLS_TEXT = {
    matchLabel: 'Match each one to',
    fixedDates: 'Set two fixed dates instead',
    searchPlaceholder: 'Search by name or email',
};

export const FOOTER_TEXT = {
    // Shown while the pairing on screen is ahead of the one the results were
    // built from — Apply is what closes that gap.
    unsaved: 'Unsaved changes — results still show the previous pairing.',
    willCompare: 'will be compared.',
    discard: 'Discard',
    apply: 'Apply changes',
};

// Two pairings are the same when the same participants carry the same
// hand-picked baseline. Compared by content, not identity: setState hands back
// a fresh object every time, so identity would read as "changed" forever.
export const isSameOverrides = (current, applied) => {
    const currentKeys = Object.keys(current);
    return currentKeys.length === Object.keys(applied).length
        && currentKeys.every((id) => current[id] === applied[id]);
};

// The hand-picked pairings as the API takes them — one entry per row that was
// re-pointed, read straight off baselineOverrides ({ current: baseline }).
// Returns null when nobody was re-pointed, so the request can leave the key off
// entirely rather than sending an empty list.
export const buildComparisonChanges = (baselineOverrides) => {
    const entries = Object.entries(baselineOverrides || {});
    if (!entries.length) return null;
    return entries.map(([currentProjectId, matchingProjectId]) => ({ currentProjectId, matchingProjectId }));
};

// The API flags which earlier cycle is "their previous 360" (isPrevious) and
// which is "their very first" (isFirst) — found by date server-side, so they
// hold whatever order the list comes back in. Head/tail is only a fallback.
export const baselineForMode = (row, matchMode) => {
    const rounds = row.earlierRounds;
    if (matchMode === MATCH_MODES.FIRST) {
        return rounds.find((round) => round.isFirst) || rounds[rounds.length - 1];
    }
    return rounds.find((round) => round.isPrevious) || rounds[0];
};

// Labels an entry in the per-row baseline dropdown by its place in that
// participant's own history. Rounds in between the two ends are unlabelled.
export const describeBaselineRound = (round) => {
    if (round.isPrevious) return 'their previous 360';
    if (round.isFirst) return 'their first 360';
    return null;
};

// Gap between the two rounds being compared, in whole months ("13 mo").
// Returns null for anything unparseable so the row can just drop the meta.
export const monthsBetween = (fromLabel, toLabel) => {
    const from = moment(fromLabel, ROUND_LABEL_FORMAT);
    const to = moment(toLabel, ROUND_LABEL_FORMAT);
    if (!from.isValid() || !to.isValid()) return null;
    return to.diff(from, 'months');
};

// Page size for both lists' server paging (`from`/`size`).
export const COMPARED_PAGE_SIZE = TREND_PAGE_SIZE;
export const NOT_COMPARED_PAGE_SIZE = TREND_PAGE_SIZE;

// Only live and closed ("complete") cycles can be compared; drafts are listed
// on the not-compared side but never count towards the two a comparison needs.
const COMPARABLE_STATUSES = ['live', 'complete'];

// How many compared leaders stay loaded when the match mode changes. Every
// loaded row has to re-render for a new mode, so the list is cut back to this
// and the rest are fetched again as the user scrolls — see trimComparedList.
export const MATCH_MODE_KEEP_ROWS = 30;

// Search goes to the server, so wait for typing to settle before re-fetching.
export const SEARCH_DEBOUNCE_MS = 400;

export const LIST_TYPES = {
    COMPARED: 'compared',
    NOT_COMPARED: 'notCompared',
};

// The two getProjectComparison calls that seed Trend Comparison — one per
// section, each starting at its own first page since the lists page apart.
export const PROJECT_COMPARISON_REQUESTS = [
    { listType: LIST_TYPES.COMPARED, size: COMPARED_PAGE_SIZE },
    { listType: LIST_TYPES.NOT_COMPARED, size: NOT_COMPARED_PAGE_SIZE },
];

// Why a leader from getProjectComparison (listType "notCompared") can't be
// compared. The API doesn't say why a leader can't be compared, only which cycles they
// have — the latest (`value`/`label`/`status`) plus `projects`, drafts
// included. The reason is read off those: fewer than two live or closed cycles.
export const notComparedReason = (leader) => {
    const cycles = [
        { label: leader.label, status: leader.status },
        ...(leader.projects || []),
    ];
    const comparable = cycles.filter((cycle) => COMPARABLE_STATUSES.includes(cycle.status));
    if (!comparable.length) return 'Only draft 360s so far — nothing live or closed to compare';
    if (cycles.length === 1) return `No earlier 360 on record — ${leader.label} was their first`;
    return `Only their ${comparable[0].label} 360 is live or closed — the rest are drafts`;
};
